import { t } from '../utils/i18n'
import styles from './_announcement.module.scss'
import { useLazyQuery, useMutation } from '@apollo/client'
import Link from 'next/link'
import Image from 'next/legacy/image'
import {
  CommunityConsolidatedNotification,
  CommunityNotificationTypeEnum
} from '../__generated__/graphql'
import {
  labCommunityCommentPath,
  labCommunityMemberPath,
  labCommunityPostPath
} from '../utils/routeHelpers'
import classNames from 'classnames'
import {
  COMMUNITY_CONSOLIDATED_NOTIFICATIONS_DETAILS_QUERY,
  COMMUNITY_PROFILE_CONSOLIDATED_NOTIFICATIONS_QUERY,
  TOGGLE_COMMUNITY_CONSOLIDATED_NOTIFICATION_READ_MUTATION
} from '../gql'
import { getOperationName } from 'apollo-boost'
import { Dispatch, SetStateAction, useRef, useState } from 'react'
import dayjs from 'dayjs'
import updateLocale from 'dayjs/plugin/updateLocale'
import relativeTime from 'dayjs/plugin/relativeTime'
import useScrollLoading from '../utils/hooks/useScrollLoading'
import LoadingRing from './LoadingRing'

dayjs.extend(relativeTime)
dayjs.extend(updateLocale)

dayjs.updateLocale('en', {
  relativeTime: {
    past: '%s ago',
    s: '%ds',
    m: '1m',
    mm: '%dm',
    h: '1h',
    hh: '%dh',
    d: '1d',
    dd: '%dd',
    M: '1m',
    MM: '%dmo',
    y: '1y',
    yy: '%dy'
  }
})

const customFromNow = (date: dayjs.ConfigType) => {
  const now = dayjs()
  const diffInDays = now.diff(date, 'day')

  if (diffInDays >= 7 && diffInDays < 30) {
    const weeks = Math.floor(diffInDays / 7)
    return `${weeks}w ago`
  }

  return dayjs(date).fromNow()
}

interface DropdownProps {
  announcements: CommunityConsolidatedNotification[]
  isRemoveReadFilter: boolean
  loadMoreAnnouncements: () => void
  isLoading: boolean
  setLabAnnouncements: Dispatch<
    SetStateAction<CommunityConsolidatedNotification[]>
  >
}

const getAnnouncementLink = (
  announcement: CommunityConsolidatedNotification
) => {
  switch (announcement.actionType) {
    case CommunityNotificationTypeEnum.PostLiked:
    case CommunityNotificationTypeEnum.NewPostFollowedCategory:
    case CommunityNotificationTypeEnum.MentionInPost:
    case CommunityNotificationTypeEnum.FollowedProfileAction:
      return labCommunityPostPath(announcement.resource.post)
    case CommunityNotificationTypeEnum.NewFollower:
      return labCommunityMemberPath(announcement.resource.user)
    case CommunityNotificationTypeEnum.CommentReply:
    case CommunityNotificationTypeEnum.CommentLiked:
    case CommunityNotificationTypeEnum.Mention:
    case CommunityNotificationTypeEnum.FollowedPostUpdated:
      return labCommunityCommentPath(
        announcement.resource.post,
        announcement.resource.comment.id
      )
  }
}

const getNotificationTitle = (
  notification: CommunityConsolidatedNotification,
  memberName?: string
) => {
  let actionPerformer
  if (memberName) {
    actionPerformer = memberName
  } else if (notification.actionPerformers.length === 1) {
    actionPerformer = notification.actionPerformers[0].firstName
  } else {
    actionPerformer = notification.actionPerformersCount + ' ' + t('people')
  }

  const topicTitle = notification.resource.post?.category.title

  switch (notification.actionType) {
    case CommunityNotificationTypeEnum.PostLiked:
      return actionPerformer + ' ' + t('liked your post')
    case CommunityNotificationTypeEnum.CommentReply:
      return actionPerformer + ' ' + t('replied to your post')
    case CommunityNotificationTypeEnum.NewFollower:
      return t('You have a new Follower')
    case CommunityNotificationTypeEnum.Mention:
      return t('Someone mentioned you')
    case CommunityNotificationTypeEnum.NewPostFollowedCategory:
      return t('New post in ') + topicTitle
    case CommunityNotificationTypeEnum.CommentLiked:
      return actionPerformer + ' ' + t('liked your comment')
    case CommunityNotificationTypeEnum.MentionInPost:
      return t('Someone mentioned you in post')
    case CommunityNotificationTypeEnum.FollowedPostUpdated:
      return t('New comment on post')
    case CommunityNotificationTypeEnum.FollowedProfileAction:
      return actionPerformer + t(' posted in ') + topicTitle
    default:
      return null
  }
}

const VISIBLE_MEMBERS_NUMBER = 4

const LabNotice: React.FC<{
  announcement: CommunityConsolidatedNotification
  setLabAnnouncements: Dispatch<
    SetStateAction<CommunityConsolidatedNotification[]>
  >
}> = ({ announcement, setLabAnnouncements }) => {
  const isExpandButtonShown =
    announcement.actionPerformersCount > VISIBLE_MEMBERS_NUMBER

  const membersShort = announcement.actionPerformers

  const [members, setMembers] = useState([])
  const [isMembersListExpanded, setIsMembersListExpanded] = useState(false)

  const [queryNotificationDetails] = useLazyQuery(
    COMMUNITY_CONSOLIDATED_NOTIFICATIONS_DETAILS_QUERY,
    {
      variables: { notificationId: announcement.id }
    }
  )

  const [toggleRead] = useMutation(
    TOGGLE_COMMUNITY_CONSOLIDATED_NOTIFICATION_READ_MUTATION,
    {
      variables: { notificationId: announcement.id },
      onCompleted: () => {
        setLabAnnouncements((announcements) =>
          announcements.map((a) =>
            a.id === announcement.id ? { ...a, read: !a.read } : a
          )
        )
      },
      refetchQueries: [
        getOperationName(COMMUNITY_PROFILE_CONSOLIDATED_NOTIFICATIONS_QUERY)
      ]
    }
  )

  const openMembersList = (e) => {
    e.preventDefault()
    queryNotificationDetails({
      onCompleted: (data) => {
        setMembers(
          data.communityConsolidatedNotificationDetails.actionPerformers
        )
        setIsMembersListExpanded((isExpanded) => !isExpanded)
      }
    })
  }

  const url = getAnnouncementLink(announcement)

  return (
    <>
      <div
        className={classNames(styles.notice, {
          [styles.noticeInactive]: announcement.read
        })}
      >
        <div
          className={classNames(styles.noticeIndicator, {
            [styles.noticeIndicatorInactive]: announcement.read
          })}
          onClick={() => toggleRead()}
        />
        <Link href={url} className={styles.noticeInfo}>
          <div
            className={classNames(styles.noticeText, styles.noticeTextNotify)}
          >
            {membersShort
              .slice(0, VISIBLE_MEMBERS_NUMBER)
              .map((member, index) => {
                return (
                  <div
                    key={member.id}
                    style={{ zIndex: membersShort.length - index }}
                    className={styles.memberPictureContainer}
                  >
                    <Image
                      key={member.id}
                      src={member.picture}
                      width={36}
                      height={36}
                      alt={`${member.firstName} ${member.lastName}`}
                      className={styles.memberPicture}
                    />
                  </div>
                )
              })}
            {isExpandButtonShown && (
              <button className={styles.moreButton} onClick={openMembersList}>
                {isMembersListExpanded ? '-' : '+'}
              </button>
            )}
            <p className={styles.noticeTitle}>
              <span>{getNotificationTitle(announcement)}</span>
            </p>
          </div>

          <p className={classNames(styles.noticeTitle, styles.noticeDate)}>
            {customFromNow(announcement.createdAt)}
          </p>
        </Link>
      </div>

      <div
        className={classNames(styles.membersListWrapper, {
          [styles.expanded]: isMembersListExpanded
        })}
      >
        <div className={styles.membersList}>
          {members.map((member) => (
            <div
              key={member.id}
              className={classNames(styles.notice, {
                [styles.noticeInactive]: announcement.read
              })}
            >
              <Link href={url}>
                <div
                  className={classNames(
                    styles.noticeText,
                    styles.noticeTextNotify
                  )}
                >
                  <div className={styles.memberPictureContainer}>
                    <Image
                      key={member.id}
                      src={member.picture}
                      width={36}
                      height={36}
                      alt={`${member.firstName} ${member.lastName}`}
                      className={classNames(styles.memberPicture)}
                    />
                  </div>

                  <p className={styles.noticeTitle}>
                    <span>
                      {getNotificationTitle(announcement, member.firstName)}
                    </span>
                  </p>
                  <p
                    className={classNames(
                      styles.noticeTitle,
                      styles.noticeDate,
                      styles.memberNoticeDate
                    )}
                  >
                    {customFromNow(member.notifiedAt)}
                  </p>
                </div>
              </Link>
            </div>
          ))}
        </div>
      </div>
    </>
  )
}

const LabAnnouncementsDropdown: React.FC<DropdownProps> = ({
  announcements,
  isRemoveReadFilter,
  loadMoreAnnouncements,
  isLoading,
  setLabAnnouncements
}) => {
  const notRead = announcements.filter((notice) => !notice.read)
  const announcementList = isRemoveReadFilter ? notRead : announcements

  const labAnnouncementsRef = useRef(null)

  useScrollLoading(labAnnouncementsRef, loadMoreAnnouncements, [announcements])

  return (
    <div className={styles.notificationItems} ref={labAnnouncementsRef}>
      {announcementList.map((announcement) => (
        <LabNotice
          key={announcement.id}
          announcement={announcement}
          setLabAnnouncements={setLabAnnouncements}
        />
      ))}
      {isLoading && <LoadingRing size={32} />}
    </div>
  )
}

export default LabAnnouncementsDropdown
